import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import { Button, Card, CopyButton, EmptyState, Input, MetricCard, Modal, Select } from '../../components/core';
import { DataTable, SpinnerBlock } from '../../components/feedback';
import { http } from '../../lib/api';
import { NAV } from './nav';
import type { TenantRecordingDto, StorageQuotaDto } from '@livestreamx/shared-types';

const fmtBytes = (b: number): string => {
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${i === 0 ? v : v.toFixed(1)} ${u[i]}`;
};

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/** Absolute URL — the only thing worth copying, since the file is same-origin. */
const absolute = (url: string): string => `${window.location.origin}${url}`;

export function RecordingsPage() {
  const [rows, setRows] = useState<TenantRecordingDto[] | null>(null);
  const [quota, setQuota] = useState<StorageQuotaDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [streamId, setStreamId] = useState('');
  const [playing, setPlaying] = useState<TenantRecordingDto | null>(null);

  useEffect(() => {
    let stopped = false;
    const tick = () => {
      http
        .get<TenantRecordingDto[]>('/api/tenant/streams/recordings')
        .then((d) => {
          if (stopped) return;
          setRows(d);
          setError(null);
        })
        .catch((err) => {
          if (stopped) return;
          setError(err instanceof Error ? err.message : 'Failed to load recordings');
          setRows([]);
        });
      // Plan storage quota — same cadence so the remaining space tracks the files.
      void http
        .get<StorageQuotaDto>('/api/tenant/streams/storage')
        .then((q) => {
          if (!stopped) setQuota(q);
        })
        .catch(() => {});
    };
    void tick();
    // A file grows while its publish session is live, so poll like Destinations
    // does — a one-shot load would freeze the size at page load.
    const timer = setInterval(tick, 15_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, []);

  const list = rows ?? [];
  const streams = useMemo(
    () => [...new Map(list.map((r) => [r.streamId, r.streamName])).entries()].sort((a, b) => a[1].localeCompare(b[1])),
    [list],
  );
  const q = search.trim().toLowerCase();
  const visible = list.filter(
    (r) =>
      (streamId === '' || r.streamId === streamId) &&
      (q === '' || r.name.toLowerCase().includes(q) || r.streamName.toLowerCase().includes(q)),
  );
  const totalBytes = visible.reduce((n, r) => n + r.sizeBytes, 0);

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="flex justify-between items-center mb-2">
        <h1 className="page-title">Recordings</h1>
        <Button variant="secondary" onClick={() => window.location.reload()}>Refresh</Button>
      </div>

      <p className="text-muted text-sm">
        Recordings are kept for {quota?.retentionDays ?? 5} days, then the file and its details are deleted
        automatically.
        {quota
          ? ` Storage: ${fmtBytes(quota.usedBytes)} of ${quota.quotaGb} GB used · ${fmtBytes(quota.remainingBytes)} remaining.`
          : ''}
      </p>

      {error ? <EmptyState title="Failed to load recordings" body={error} /> : null}
      {!rows ? <SpinnerBlock label="Loading recordings…" /> : null}

      {rows && rows.length > 0 ? (
        <>
          <div className="stats-grid">
            <MetricCard label="Files" value={visible.length} hint={`${list.length} total`} />
            <MetricCard label="Total size" value={fmtBytes(totalBytes)} hint="Of the current filter" />
            <MetricCard label="Streams" value={streams.length} hint="With recordings on file" />
          </div>

          <Card
            title="Library"
            action={
              <div className="flex gap-2 items-end">
                <Input label="Search" value={search} onChange={setSearch} placeholder="File or stream name" />
                <Select
                  label="Stream"
                  value={streamId}
                  onChange={setStreamId}
                  options={[{ value: '', label: 'All streams' }, ...streams.map(([id, name]) => ({ value: id, label: name }))]}
                />
              </div>
            }
          >
            {visible.length === 0 ? (
              <EmptyState title="Nothing matches" body="No recording matches this filter." />
            ) : (
              <DataTable<TenantRecordingDto>
                rows={visible}
                columns={[
                  { key: 'file', header: 'File', render: (r) => <span className="sd-mono">{r.name}</span> },
                  {
                    key: 'stream',
                    header: 'Stream',
                    render: (r) => <Link to={`/streams/${r.streamSlug || r.streamId}`}>{r.streamName}</Link>,
                  },
                  { key: 'size', header: 'Size', render: (r) => fmtBytes(r.sizeBytes) },
                  { key: 'when', header: 'Last written', render: (r) => fmtWhen(r.recordedAt) },
                  {
                    key: 'actions',
                    header: '',
                    render: (r) => (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setPlaying(r)}>▶ Play</Button>
                        <a className="btn secondary sm" href={r.url} download={r.name}>⬇ Download</a>
                        <CopyButton text={absolute(r.url)} label="Copy link" />
                        <a className="btn ghost sm" href={r.url} target="_blank" rel="noreferrer" title="Open in a new tab">↗</a>
                      </div>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      ) : null}

      {rows && rows.length === 0 ? (
        <EmptyState
          title="No recordings yet"
          body="Every publish session is recorded automatically and kept for 5 days. Files appear here as soon as your encoder connects."
          action={<Link to="/streams"><Button>Go to streams</Button></Link>}
        />
      ) : null}

      {playing ? (
        <Modal title={`${playing.streamName} — ${playing.name}`} onClose={() => setPlaying(null)}>
          <div className="sd-player">
            <video src={playing.url} controls autoPlay playsInline className="sd-player-video" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <CopyButton text={absolute(playing.url)} label="Copy link" />
            <a className="btn secondary" href={playing.url} download={playing.name}>
              ⬇ Download ({fmtBytes(playing.sizeBytes)})
            </a>
          </div>
        </Modal>
      ) : null}
    </AppLayout>
  );
}